import React, { useState, useEffect } from 'react';
import { CountrySelect } from './CountrySelect';
import StateSelect, { CANADA_STATE_CODES, US_STATE_CODES } from './StateSelect';
import GoogleAutoCompleteField from './GoogleAutoCompleteField';
import { Box } from '@mui/material';
import styles from './AddressFieldSet.module.scss';
import classNames from 'classnames';
import { Country, State } from '#types';
import MuiTextFieldText from '../MuiTextField/MuiTextFieldText/MuiTextFieldText';

export type AddressFieldsProps = {
	address: {
		addressLine1?: string;
		addressLine2?: string;
		city?: string;
		state?: State;
		postalCode?: string;
		country?: Country;
	};
	validate?: {
		addressLine1?: string;
		addressLine2?: string;
		city?: string;
		state?: string;
		postalCode?: string;
		country?: string;
	};
	onChange: ( values: Record<string, any> ) => void;
	onBlur?: ( e: React.FocusEvent<HTMLInputElement> ) => void;
	className?: string;
	disabled?: boolean;
};

export const AddressFieldSet: React.FC<AddressFieldsProps> = ( {
	address,
	validate,
	onChange,
	onBlur,
	className,
	disabled,
} ) => {
	const [ place, setPlace ] = useState<google.maps.places.PlaceResult | null>(
		null
	);

	useEffect( () => {
		if ( place?.address_components ) {
			const streetNumber = place.address_components.find( ( component ) =>
				component.types.includes( 'street_number' )
			)?.long_name;
			const streetName = place.address_components.find( ( component ) =>
				component.types.includes( 'route' )
			)?.long_name;
			const addressLine1 = `${ streetNumber } ${ streetName }`;
			const addressLine2 =
				place.address_components.find( ( component ) =>
					component.types.includes( 'subpremise' )
				)?.long_name || '';
			const city =
				place.address_components.find( ( component ) =>
					component.types.includes( 'locality' )
				)?.long_name || '';
			const state =
				place.address_components.find( ( component ) =>
					component.types.includes( 'administrative_area_level_1' )
				)?.short_name || '';
			const postalCode =
				place.address_components.find( ( component ) =>
					component.types.includes( 'postal_code' )
				)?.long_name || '';
			const country =
				place.address_components.find( ( component ) =>
					component.types.includes( 'country' )
				)?.short_name || '';
			onChange( {
				...address,
				addressLine1,
				addressLine2,
				city,
				state,
				postalCode,
				country,
			} );
		} // excluding address to avoid endless rerender
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ place ] );

	// Keep state and country in sync: set country from state when country is empty; clear state when country is set and state doesn't belong to it
	useEffect( () => {
		if ( !address.state ) {
			return;
		}
		const stateUpper = address.state.toUpperCase();
		const isCanadaState = ( CANADA_STATE_CODES as readonly string[] ).includes(
			stateUpper
		);
		const isUSState = ( US_STATE_CODES as readonly string[] ).includes(
			stateUpper
		);
		if ( address.country === 'CA' && !isCanadaState ) {
			// User switched to Canada but state is US; clear state so they can pick a province
			onChange( { ...address, state: undefined } );
		} else if ( address.country === 'US' && !isUSState ) {
			// User switched to US but state is Canadian; clear state so they can pick a state
			onChange( { ...address, state: undefined } );
		} else if ( !address.country ) {
			// No country yet; set it from the selected state
			if ( isCanadaState ) {
				onChange( { ...address, country: 'CA' } );
			} else if ( isUSState ) {
				onChange( { ...address, country: 'US' } );
			}
		} else if ( !isCanadaState && !isUSState ) {
			// State not in our lists (e.g. from API or place autocomplete); clear to keep form consistent
			onChange( { ...address, state: undefined } );
		}
		// Only run when country or state changes
	}, [ address, onChange ] );

	return (
		<Box className={ classNames( styles.container, className ) }>
			<CountrySelect
				id='country'
				label='Country'
				value={ address.country || '' }
				onChange={ ( _, country ) => onChange( { ...address, country } ) }
				className={ styles.input }
				helperText={ validate?.country }
				error={ validate?.country ? true : false }
				onBlur={ onBlur }
				textFieldSlotProps={ {
					htmlInput: {
						'data-cy': 'country',
					},
				} }
				disabled={ disabled }
				variant='standard'
			/>
			<GoogleAutoCompleteField
				id='address-line1'
				label='Address 1'
				placeholder='Enter an address'
				setPlace={ setPlace }
				onChange={ ( addressLine1 ) =>
					onChange( { ...address, addressLine1: addressLine1 || '' } )
				}
				value={ address?.addressLine1 || '' }
				className={ styles.input }
				helperText={ validate?.addressLine1 }
				error={ validate?.addressLine1 ? true : false }
				onBlur={ onBlur }
				textFieldSlotProps={ { htmlInput: { 'data-cy': 'addressLine1' } } }
				country={ address.country }
				variant='standard'
				disabled={ disabled }
			/>
			<MuiTextFieldText
				id='address-line2'
				label='Address 2'
				placeholder='Line 2'
				variant='standard'
				className={ styles.input }
				value={ address.addressLine2 || '' }
				fullWidth={ true }
				onChange={ ( e ) =>
					onChange( { ...address, addressLine2: e.target.value || '' } )
				}
				errorText={ validate?.addressLine2 }
				onBlur={ onBlur }
				slotProps={ { input: { inputProps: { 'data-cy': 'addressLine2' } } } }
				disabled={ disabled }
			/>
			<MuiTextFieldText
				id='city'
				label='City'
				placeholder='City'
				variant='standard'
				className={ styles.input }
				value={ address.city || '' }
				fullWidth={ true }
				onChange={ ( e ) => onChange( { ...address, city: e.target.value || '' } ) }
				errorText={ validate?.city }
				onBlur={ onBlur }
				slotProps={ { input: { inputProps: { 'data-cy': 'city' } } } }
				disabled={ disabled }
			/>
			<Box className={ styles.twoColumnDesktop }>
				<StateSelect
					id='state'
					label='State / Province'
					variant='standard'
					className={ styles.input }
					value={ address.state || '' }
					country={ address.country }
					onChange={ ( _, state ) => onChange( { ...address, state } ) }
					helperText={ validate?.state }
					error={ validate?.state ? true : false }
					onBlur={ onBlur }
					textFieldSlotProps={ { htmlInput: { 'data-cy': 'state' } } }
					disabled={ disabled }
				/>
				<MuiTextFieldText
					id='postal-code'
					label='Postal code'
					placeholder='Postal code'
					className={ styles.input }
					value={ address.postalCode || '' }
					fullWidth={ true }
					onChange={ ( e ) =>
						onChange( { ...address, postalCode: e.target.value || '' } )
					}
					errorText={ validate?.postalCode }
					onBlur={ onBlur }
					slotProps={ { input: { inputProps: { 'data-cy': 'postalCode' } } } }
					disabled={ disabled }
					variant='standard'
				/>
			</Box>
		</Box>
	);
};
